/**
 * =============================================================================
 * IDS CRM Web - Customers List Page
 * =============================================================================
 *
 * PURPOSE:
 * Lists all end customers (farms) with clear hierarchy showing:
 * - Direct customers (IDS sells and owns relationship)
 * - Distributor customers (sold via distributor channel)
 *
 * Distributors are managed separately on the Distributors page.
 *
 * Status tabs (Gareth 2026-09-14): All / Prospect / Active / Dormant, one at a
 * time, opening on Active and kept in the link as ?status=. Counts show only
 * when the API returns meta.status_counts.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LogCommercialActivityModal from '../components/LogCommercialActivityModal';
import CustomerStatusChip from '../components/CustomerStatusChip';
import { CUSTOMER_STATUS_ORDER, CUSTOMER_STATUS_META, newCustomerStatusForTab } from '../utils/customerStatus';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Customers.css';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  ...CUSTOMER_STATUS_ORDER.map(status => ({ key: status, label: CUSTOMER_STATUS_META[status].label }))
];
const STATUS_TAB_KEYS = STATUS_TABS.map(statusTab => statusTab.key);
const DEFAULT_STATUS_TAB = 'active';

function Customers() {
  const { authFetch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Coming back from a customer (a "←" link or the browser's Back) shows the
  // list as it was left; a fresh visit starts from the address.
  const memory = useListMemory('/customers');

  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [channelSummary, setChannelSummary] = useState({ direct: 0, distributor: 0 });
  // Per-status tab counts; null when the API does not send them.
  const [statusCounts, setStatusCounts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => memory.initial('search', searchParams.get('search') || ''));
  // Set when a reply shows the page asked for is past the last page there is.
  const [pastLastPage, setPastLastPage] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const channel = searchParams.get('channel') || '';
  const health = searchParams.get('health') || '';
  // No ?status= opens on Active; so does a retired value from an old bookmark.
  const requestedStatus = searchParams.get('status');
  const tab = STATUS_TAB_KEYS.includes(requestedStatus) ? requestedStatus : DEFAULT_STATUS_TAB;

  // An old link such as ?status=at_risk loses the unknown value, so the link
  // matches the tab it opened on.
  useEffect(() => {
    if (requestedStatus !== null && !STATUS_TAB_KEYS.includes(requestedStatus)) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
    }
  }, [requestedStatus, searchParams, setSearchParams]);

  // The tab, channel, health and page are in the address, which is remembered
  // with it. The search box filters as it is typed, before Search puts it in
  // the address, so its text is remembered here.
  useRememberList('/customers', { search });

  const fetchCustomers = useCallback(async () => {
    // A failed load says so, instead of reading as an empty list.
    const showLoadError = (message) => {
      setCustomers([]);
      setPagination({ page: 1, total: 0, totalPages: 0 });
      setChannelSummary({ direct: 0, distributor: 0 });
      setStatusCounts(null);
      setLoadError(message);
    };

    setLoading(true);
    let steppingBack = false;
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25',
        // Every end customer, not distributors. Not type=customer: a Prospect is
        // typed prospect and a Dormant customer former.
        exclude_type: 'distributor',
        include: 'status_counts'
      });

      if (search) params.append('search', search);
      if (health) params.append('health', health);
      if (tab !== 'all') params.append('status', tab);
      if (channel) params.append('channel', channel);

      const response = await authFetch(`/api/v1/customers?${params}`);
      const data = await response.json();

      if (response.ok) {
        // A page past the end (the list got shorter while a customer was
        // open) steps back to the last page there is: see the effect below.
        // The list stays loading until that page arrives, so it never reads
        // as empty.
        const lastPage = Math.max(1, data.pagination?.totalPages || 0);
        if (page > lastPage) {
          steppingBack = true;
          setPastLastPage({ page, lastPage });
          return;
        }
        setCustomers(data.data);
        setPagination(data.pagination);
        setChannelSummary(data.meta?.channel_counts || { direct: 0, distributor: 0 });
        setStatusCounts(data.meta?.status_counts || null);
        setLoadError(null);
      } else {
        showLoadError(data.message || 'Failed to load customers');
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      showLoadError('Failed to load customers');
    } finally {
      if (!steppingBack) setLoading(false);
    }
  }, [authFetch, page, search, health, tab, channel]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // The page is in the address, so the step back is made there, in place of
  // the page that no longer exists (the browser's Back does not return to
  // it). Only if the address is still on the page the reply was for.
  useEffect(() => {
    if (!pastLastPage) return;
    setPastLastPage(null);
    if (page !== pastLastPage.page) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(pastLastPage.lastPage));
    setSearchParams(newParams, { replace: true });
  }, [pastLastPage, page, searchParams, setSearchParams]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCustomers();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (search) {
      newParams.set('search', search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
  };

  const getHealthClass = (score) => {
    if (score === 'green') return 'health-badge green';
    if (score === 'amber') return 'health-badge amber';
    if (score === 'red') return 'health-badge red';
    return 'health-badge';
  };

  const getChannelInfo = (customer) => {
    if (customer.sale_channel === 'direct') {
      return {
        label: 'Direct',
        class: 'channel-direct',
        description: 'IDS Direct'
      };
    } else if (customer.master_distributor_name) {
      return {
        label: 'Via Distributor',
        class: 'channel-distributor',
        description: customer.sub_distributor_name
          ? `${customer.master_distributor_name} → ${customer.sub_distributor_name}`
          : customer.master_distributor_name
      };
    }
    return {
      label: 'Direct',
      class: 'channel-direct',
      description: 'IDS Direct'
    };
  };

  const getCorporateAddress = (customer) => [
    customer.address_line_1,
    customer.address_line_2,
    customer.town_city,
    customer.county_region,
    customer.state_province,
    customer.postcode
  ].filter(Boolean).join(', ');

  const directCount = channelSummary.direct || 0;
  const distributorCount = channelSummary.distributor || 0;
  // The subtitle names the tab ("42 dormant farms"); All has no status word.
  const farmWord = pagination.total === 1 ? 'farm' : 'farms';
  const farmsLabel = tab === 'all' ? farmWord : `${tab} ${farmWord}`;
  const hasOtherFilters = Boolean(search || channel || health);

  return (
    <div className="customers-page">
      <div className="page-header">
        <div>
          <h1>End Customers</h1>
          <p className="page-subtitle">
            {pagination.total} {farmsLabel} · {directCount} direct · {distributorCount} via distributors
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-primary btn-activity" onClick={() => setShowActivityModal(true)}>
            + Log Activity
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowScheduleModal(true)}>
            Schedule Activity
          </button>
          <Link to={`/customers/new?status=${newCustomerStatusForTab(tab)}`} className="btn-primary">
            + New Customer
          </Link>
        </div>
      </div>

      {/* Channel hierarchy filter buttons */}
      <div className="hierarchy-info">
        <button
          className={`hierarchy-card direct ${channel === 'direct' ? 'active' : ''} ${channel === '' ? '' : channel !== 'direct' ? 'inactive' : ''}`}
          onClick={() => handleFilterChange('channel', channel === 'direct' ? '' : 'direct')}
        >
          <div className="hierarchy-icon">IDS</div>
          <div className="hierarchy-arrow">→</div>
          <div className="hierarchy-icon customer">Farm</div>
          <div className="hierarchy-label">
            <strong>Direct</strong>
            <span>IDS sells and supports directly</span>
          </div>
        </button>
        <button
          className={`hierarchy-card distributor ${channel === 'distributor' ? 'active' : ''} ${channel === '' ? '' : channel !== 'distributor' ? 'inactive' : ''}`}
          onClick={() => handleFilterChange('channel', channel === 'distributor' ? '' : 'distributor')}
        >
          <div className="hierarchy-icon">IDS</div>
          <div className="hierarchy-arrow">→</div>
          <div className="hierarchy-icon dist">Dist</div>
          <div className="hierarchy-arrow">→</div>
          <div className="hierarchy-icon customer">Farm</div>
          <div className="hierarchy-label">
            <strong>Via Distributor</strong>
            <span>Sold/supported through partner</span>
          </div>
        </button>
      </div>

      {/* Status tabs: one status at a time. Every tab, All included, is
          written into the link, so a copied link reopens the same tab. */}
      <div className="status-filter-tabs customer-status-tabs" role="group" aria-label="Customer status">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`status-filter-tab ${tab === key ? 'active' : ''}`}
            aria-pressed={tab === key}
            onClick={() => handleFilterChange('status', key)}
          >
            {label}
            {statusCounts && (
              <>
                {' '}<span className="status-tab-count">{statusCounts[key] ?? 0}</span>
              </>
            )}
          </button>
        ))}
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, number, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>

        {/* Channel is the Direct / Via Distributor cards above; status is the tabs. */}
        <div className="filters">
          <select
            value={health}
            onChange={(e) => handleFilterChange('health', e.target.value)}
            className="filter-select"
          >
            <option value="">All Health</option>
            <option value="green">Healthy</option>
            <option value="amber">Attention</option>
            <option value="red">Urgent</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading customers...</div>
      ) : loadError ? (
        <div className="empty-state" role="alert">
          <p>{loadError}</p>
        </div>
      ) : customers.length === 0 ? (
        <div className="empty-state">
          <p>{tab === 'all' ? 'No customers found' : `No ${tab} customers found`}</p>
          {hasOtherFilters && <p>Try another tab or clear the filters</p>}
        </div>
      ) : (
        <>
          <div className="customers-table">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Channel</th>
                  <th>Location</th>
                  <th>Sites</th>
                  <th>Herd</th>
                  <th>Assets</th>
                  <th>Health</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => {
                  const channelInfo = getChannelInfo(customer);
                  return (
                    <tr key={customer.customer_id}>
                      <td>
                        <div className="customer-cell">
                          <div className="customer-status-name-row">
                            <Link to={`/customers/${customer.customer_id}`} className="customer-name">
                              {customer.business_name}
                            </Link>
                            {/* Only All mixes statuses; on the others the tab says it. */}
                            {tab === 'all' && <CustomerStatusChip status={customer.status} />}
                          </div>
                          <span className="customer-number">{customer.customer_number}</span>
                        </div>
                      </td>
                      <td>
                        <div className="channel-cell">
                          <span className={`channel-badge ${channelInfo.class}`}>
                            {channelInfo.label}
                          </span>
                          {channelInfo.description !== 'IDS Direct' && (
                            <span className="channel-desc">{channelInfo.description}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="location-cell">
                          <span className="location-main">
                            {getCorporateAddress(customer) || '-'}
                          </span>
                          <span className="location-country">
                            Corporate address{customer.country_code ? ` · ${customer.country_code}` : ''}
                          </span>
                        </div>
                      </td>
                      <td>
                        {Number(customer.site_count) > 0 ? (
                          <Link to={`/customers/${customer.customer_id}?tab=sites`} className="site-count-link">
                            {customer.site_count} {Number(customer.site_count) === 1 ? 'site' : 'sites'}
                          </Link>
                        ) : (
                          <Link to={`/customers/${customer.customer_id}/sites/new`} className="site-count-link muted">
                            Add site
                          </Link>
                        )}
                      </td>
                      <td>
                        {customer.cows_milking ? (
                          <span className="herd-count">{customer.cows_milking}</span>
                        ) : '-'}
                      </td>
                      <td>
                        {customer.asset_count ? (
                          <Link to={`/assets?customer_id=${customer.customer_id}`} className="asset-count">
                            {customer.asset_count}
                          </Link>
                        ) : '-'}
                      </td>
                      <td>
                        <span
                          className={getHealthClass(customer.health_score)}
                          title={customer.health_score_reason || ''}
                        >
                          {customer.health_score || '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= pagination.totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showActivityModal && (
        <LogCommercialActivityModal
          authFetch={authFetch}
          onClose={() => setShowActivityModal(false)}
          onSaved={() => {
            setShowActivityModal(false);
            fetchCustomers();
          }}
        />
      )}

      {showScheduleModal && (
        <LogCommercialActivityModal
          authFetch={authFetch}
          mode="schedule"
          onClose={() => setShowScheduleModal(false)}
          onSaved={() => {
            setShowScheduleModal(false);
            fetchCustomers();
          }}
        />
      )}
    </div>
  );
}

export default Customers;
